import React, { useEffect, useState, useCallback } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    TableContainer,
    Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import { FieldArray, useFormikContext, getIn } from "formik";
import PayrollsPopup from "../../../Components/PayrollPopup";
import AddJobTitle from "../../../QuickAdd/AddJobTitle";
import { Dropdown } from "../../../HelperFunctions/PayrollDropdown";
import AddCOA from "../../../QuickAdd/AddCOA";
import { useAlerts, useCOAOptions } from "../../../Hooks";
import { payrollSetupStyles } from "../../../styles/payrollSetup.styles";

const EXCLUDED_ACCOUNT_TYPES = new Set([
    "accounts payable",
    "accounts receivable"
]);

const filterAccounts = (accounts) => {
    if (!accounts) return [];
    return accounts.filter(acc => 
        !EXCLUDED_ACCOUNT_TYPES.has((acc.type || acc.AccountTypeName || "").toLowerCase())
    );
};

// --- Item Type Helpers ---
const isWageItem = (type) => type === "wages";
const isEmployerTaxItem = (type) => type === "employer_taxes";

// --- Render Helpers & Constants ---
const getSelectedOption = (options, fieldValue, fieldName, isLoading) => {
    const defaultOpt = { label: "--Select--", value: "" };
    if (!fieldValue) return defaultOpt;

    const allOptions = [defaultOpt, ...options];
    const found = allOptions.find(opt => String(opt.value) === String(fieldValue));
    return found || { value: fieldValue, label: fieldName || (isLoading ? "" : fieldValue) };
};

const getSelectedCoaOption = (options, coaValue, coaName, isLoading) => {
    const defaultOpt = { label: "--Select--", value: "" };
    if (!coaValue) return defaultOpt;

    const allOptions = [defaultOpt, { label: "Add New", value: "ADD_NEW" }, ...options];
    const found = allOptions.find(opt => String(opt.value) === String(coaValue));
    return found || { value: coaValue, label: coaName || (isLoading ? "" : coaValue) };
};

const renderJobOption = (props, option, { selected }) => {
    const { key, ...restProps } = props;
    const isAddNew = option.value === "ADD_NEW" && !selected;
    const label = option.label || option.Name || "";
    return (
        <li key={key} {...restProps} title={label} style={{ ...(isAddNew ? { color: "#d32f2f" } : {}) }}>
            <span title={label}>{label}</span>
        </li>
    );
};

const renderCoaOption = (props, option, { selected }) => {
    const { key, ...restProps } = props;
    const label = option.displayLabel || option.label || option.Name || "";
    if (option.value === "ADD_NEW") {
        return (
            <li key={key} {...restProps} title={label} style={{ color: "#d32f2f", fontWeight: 500 }}>
                <span title={label}>{label}</span>
            </li>
        );
    }
    return (
        <li key={key} {...restProps} title={label}>
            <Box display="flex" alignItems="center" width="100%" minWidth={0}>
                <Typography fontSize={11} sx={payrollSetupStyles.coaOptionLabel}>
                    <span title={label}>{label}</span>
                </Typography>
                {option.type && (
                    <Typography
                        fontSize={11}
                        ml={1}
                        sx={payrollSetupStyles.getCoaOptionTypeSx(selected)}
                    >
                        <span title={option.type}>{option.type}</span>
                    </Typography>
                )}
            </Box>
        </li>
    );
};

const COAConfigurations = ({ corpId, itemType, initialEntries, onLoadingChange, prefillData }) => {
    const { values, setFieldValue, errors, touched, submitCount } = useFormikContext();
    const { enqueueSnackbar } = useSnackbar();

    const { popup: alertPopup, showDeleteConfirm, showErrorPopup, hidePopup } = useAlerts();

    const [openAddJobTitle, setOpenAddJobTitle] = useState(false);
    const [pendingJobTitleField, setPendingJobTitleField] = useState("");

    const [openAddCOA, setOpenAddCOA] = useState(false);
    const [pendingCOAField, setPendingCOAField] = useState("");

    const { expenseCoaOptions: allExpenseCoa, payableCoaOptions: allPayableCoa, departmentOptions, jobTitleOptions, isLoading, loadJobTitles, loadAccounts } = useCOAOptions(corpId);

    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(isLoading);
        }
    }, [isLoading, onLoadingChange]);

    useEffect(() => {
        // When used in the Update form, this will populate the grid with existing data.
        if (initialEntries && initialEntries.length > 0) {
            setFieldValue("entries", initialEntries);
        }
    }, [initialEntries, setFieldValue]);

    useEffect(() => {
        if (typeof errors.entries === "string") {
            enqueueSnackbar(errors.entries, { variant: "error" });
        }
    }, [errors.entries, submitCount, enqueueSnackbar]);

    // --- Event Handlers ---
    const handleJobTitleChange = useCallback((name, newValue) => {
        if (newValue?.value === "ADD_NEW") {
            setPendingJobTitleField(name);
            setOpenAddJobTitle(true);
        } else {
            setFieldValue(name, newValue?.value || "");
        }
    }, [setFieldValue]);

    const handleCoaChange = useCallback((name, newValue, oppositeFieldName) => {
        if (newValue?.value === "ADD_NEW") {
            setPendingCOAField(name);
            setOpenAddCOA(true);
            return;
        }
        const selected = newValue?.value || "";
        const oppositeValue = getIn(values, oppositeFieldName);
        if (selected && selected === oppositeValue) {
            showErrorPopup("Expense COA and Payable COA cannot be the same.");
            return;
        }
        setFieldValue(name, selected);
        setFieldValue(name.replace("Coa", "CoaName"), newValue?.name || newValue?.label || newValue?.Name || "");
    }, [values, setFieldValue, showErrorPopup]);

    const handleDeleteRow = useCallback((index, remove) => {
        if (values.entries && values.entries.length <= 1) {
            showErrorPopup("At least one mapping row is required.");
            return;
        }
        const rowData = values.entries[index];
        if (rowData.isUsedEmp) {
            enqueueSnackbar("This job department is currently being used in employee master.", { variant: "error" });
            return;
        }
        const hasData = rowData.department || rowData.jobTitle || rowData.expenseCoa || rowData.payableCoa;
        if (hasData) {
            showDeleteConfirm("Are you sure you want to delete this row?", () => remove(index));
        } else {
            remove(index);
        }
    }, [values.entries, showErrorPopup, enqueueSnackbar, showDeleteConfirm]);

    const expenseCoaOptions = filterAccounts(allExpenseCoa);
    const payableCoaOptions = filterAccounts(allPayableCoa);

    return (
        <>
            <FieldArray name="entries">
                {({ push, remove }) => (
                    <Box width={"70%"} height={"100%"}>

                        <TableContainer
                            sx={payrollSetupStyles.coaTableContainer}
                        >
                            <Table size="small" stickyHeader>
                                <TableHead
                                    sx={payrollSetupStyles.coaTableHead}
                                >
                                    <TableRow>
                                        <TableCell>Department</TableCell>
                                        <TableCell>Job Title</TableCell>
                                        <TableCell>Expense Account</TableCell>

                                        {isEmployerTaxItem(itemType) && (
                                            <TableCell>Payable Account</TableCell>
                                        )}

                                        <TableCell width={0} align="right">
                                            <Button
                                                sx={payrollSetupStyles.coaAddRowBtn}
                                                startIcon={<AddIcon />}
                                                size="small"
                                                onClick={() => {
                                                    push({
                                                        department: "",
                                                        jobTitle: "",
                                                        expenseCoa: "",
                                                        payableCoa: "",
                                                    });
                                                }}
                                            >
                                                Add New
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {values.entries.map((_, index) => (
                                        <TableRow key={index} sx={payrollSetupStyles.tableRowHover}>
                                            
                                            {/* Department */}
                                            <TableCell>
                                                <Dropdown
                                                    label=""
                                                    name={`entries[${index}].department`}
                                                    value={getSelectedOption(departmentOptions, getIn(values, `entries[${index}].department`), getIn(values, `entries[${index}].departmentName`), isLoading)}
                                                    loading={isLoading}
                                                    options={[{ label: "--Select--", value: "" }, ...departmentOptions]}
                                                    getOptionLabel={(option) => option?.label || option?.Name || ""}
                                                    isEqual={(option, value) => String(option.value) === String(value?.value)}
                                                    disabled={(index === 0 && !!prefillData?.departmentId)}
                                                    onChange={(name, newValue) => setFieldValue(name, newValue?.value || "")}
                                                    error={getIn(touched, `entries[${index}].department`) && !getIn(values, `entries[${index}].department`)}
                                                    sx={payrollSetupStyles.getCoaDropdownSx((index === 0 && !!prefillData?.departmentId))}
                                                />
                                            </TableCell>

                                            {/* Job Title */}
                                            <TableCell>
                                                <Dropdown
                                                    label=""
                                                    name={`entries[${index}].jobTitle`}
                                                    value={getSelectedOption(jobTitleOptions, getIn(values, `entries[${index}].jobTitle`), getIn(values, `entries[${index}].jobTitleName`), isLoading)}
                                                    loading={isLoading}
                                                    options={[{ label: "--Select--", value: "" }, ...jobTitleOptions]}
                                                    getOptionLabel={(option) => option?.label || option?.Name || ""}
                                                    isEqual={(option, value) => String(option.value) === String(value?.value)}
                                                    disabled={(index === 0 && !!prefillData?.jobId)}
                                                    renderOption={renderJobOption}
                                                    onChange={handleJobTitleChange}
                                                    error={isWageItem(itemType) && getIn(touched, `entries[${index}].jobTitle`) && !getIn(values, `entries[${index}].jobTitle`)}
                                                    sx={payrollSetupStyles.getCoaDropdownSx((index === 0 && !!prefillData?.jobId))}
                                                />
                                            </TableCell>

                                            {/* Expense COA */}
                                            <TableCell>
                                                <Dropdown
                                                    label=""
                                                    name={`entries[${index}].expenseCoa`}
                                                    value={getSelectedCoaOption(expenseCoaOptions, getIn(values, `entries[${index}].expenseCoa`), getIn(values, `entries[${index}].expenseCoaName`), isLoading)}
                                                    loading={isLoading}
                                                    options={[{ label: "--Select--", value: "" }, { label: "Add New", value: "ADD_NEW" }, ...expenseCoaOptions]}
                                                    getOptionLabel={(option) => option?.label || option?.Name || ""}
                                                    isEqual={(option, value) => String(option.value) === String(value?.value)}
                                                    renderOption={renderCoaOption}
                                                    onChange={(name, newValue) => handleCoaChange(name, newValue, `entries[${index}].payableCoa`)}
                                                    error={getIn(touched, `entries[${index}].expenseCoa`) && !getIn(values, `entries[${index}].expenseCoa`)}
                                                    sx={payrollSetupStyles.coaDropdownMinWidth}
                                                    popperSx={payrollSetupStyles.coaPopperSx}
                                                />
                                            </TableCell>

                                            {/* Payable COA (for employer taxes only) */}
                                            {isEmployerTaxItem(itemType) && (
                                                <TableCell>
                                                    <Dropdown
                                                        label=""
                                                        name={`entries[${index}].payableCoa`}
                                                        value={getSelectedCoaOption(payableCoaOptions, getIn(values, `entries[${index}].payableCoa`), getIn(values, `entries[${index}].payableCoaName`), isLoading)}
                                                        loading={isLoading}
                                                        options={[{ label: "--Select--", value: "" }, { label: "Add New", value: "ADD_NEW" }, ...payableCoaOptions]}
                                                        getOptionLabel={(option) => option?.label || option?.Name || ""}
                                                        isEqual={(option, value) => String(option.value) === String(value?.value)}
                                                        renderOption={renderCoaOption}
                                                        onChange={(name, newValue) => handleCoaChange(name, newValue, `entries[${index}].expenseCoa`)}
                                                        error={getIn(touched, `entries[${index}].payableCoa`) && !getIn(values, `entries[${index}].payableCoa`)}
                                                        sx={payrollSetupStyles.coaDropdownMinWidth}
                                                        popperSx={payrollSetupStyles.coaPopperSx}
                                                    />
                                                </TableCell>
                                            )}

                                            {/* Delete row */}
                                            <TableCell align="center">
                                                <IconButton
                                                    onClick={() => handleDeleteRow(index, remove)}
                                                    sx={payrollSetupStyles.coaDeleteRowBtn}
                                                >
                                                    <CloseIcon sx={payrollSetupStyles.coaDeleteRowCloseIcon} />
                                                </IconButton>
                                            </TableCell>

                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}
            </FieldArray>
            <PayrollsPopup
                open={alertPopup.open}
                popupType={alertPopup.type}
                message={alertPopup.message}
                onConfirm={alertPopup.onConfirm}
                onCancel={hidePopup}
            />
            <AddJobTitle
                open={openAddJobTitle}
                onClose={() => {
                    setOpenAddJobTitle(false);
                    setPendingJobTitleField("");
                }}
                onSuccess={async (createdJobTitle) => {
                    const titles = await loadJobTitles();

                    if (!pendingJobTitleField) {
                        return;
                    }

                    const createdId = createdJobTitle?.value ? String(createdJobTitle.value) : "";
                    const createdName = (createdJobTitle?.label || "").trim().toLowerCase();

                    const matchedTitle =
                        titles.find((item) => String(item.value) === createdId) ||
                        titles.find((item) => (item.label || "").trim().toLowerCase() === createdName);

                    if (matchedTitle?.value) {
                        setFieldValue(pendingJobTitleField, matchedTitle.value);
                    }
                }}
            />
            <AddCOA 
                open={openAddCOA}
                corporationId={values.corporation?.corpValue !== undefined && values.corporation?.corpValue !== null ? values.corporation.corpValue : ""}
                corporationName={values.corporation?.corpName !== undefined && values.corporation?.corpName !== null ? values.corporation.corpName : ""}
                onClose={() => {
                    setOpenAddCOA(false);
                    setPendingCOAField("");
                }}
                // Pass formik context down for direct updates
                setFieldValue={setFieldValue}
                pendingCOAField={pendingCOAField}
                onSuccess={async () => {
                    // The only thing left to do here is refresh the dropdown options
                    await loadAccounts();
                }}
            />
        </>
    );
};

export default COAConfigurations;
